import { Box, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import SectionHeader from '../SectionHeader'
import FilterPill from './FilterPill'
import { MULTI_VERSIONS } from './resultsMockData'

type Version = (typeof MULTI_VERSIONS)[number]

/** Side-by-side comparison of the 3 ad versions: preview, mini timeline, KPIs. */
export default function MultiHero() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')

  return (
    <Box sx={{ bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '18px', p: 2.75, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <SectionHeader
        title={t('multi.hero.title')}
        subtitle={t('multi.hero.sub')}
        right={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: 12, color: c.ink56 }}>{t('multi.hero.metric')}</Typography>
            <FilterPill label={t('multi.hero.showLabel')} value={t('multi.hero.showValue')} active />
          </Box>
        }
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {MULTI_VERSIONS.map((v) => (
          <VersionCard key={v.letter} v={v} />
        ))}
      </Box>
    </Box>
  )
}

function VersionCard({ v }: { v: Version }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  const accent = theme.affexy.emotion[v.colorKey]

  return (
    <Stack
      spacing={1.5}
      sx={{
        bgcolor: v.winner ? alpha(c.coral, 0.04) : c.gray50,
        border: `1.5px solid ${v.winner ? alpha(c.coral, 0.35) : c.ink08}`,
        borderRadius: '16px',
        p: 2,
        position: 'relative',
      }}
    >
      {v.winner && (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            px: 1.125,
            py: 0.375,
            borderRadius: `${theme.affexy.radii.pill}px`,
            bgcolor: c.coral,
            color: '#fff',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
          }}
        >
          {t('multi.leader')}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: accent,
            color: '#fff',
            fontFamily: theme.affexy.fonts.heading,
            fontWeight: 700,
            fontSize: 14,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {v.letter}
        </Box>
        <Stack spacing={0}>
          <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 14, color: c.ink, lineHeight: 1.2 }}>
            {t(`multi.versions.${v.nameKey}`)}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: c.ink56, fontVariantNumeric: 'tabular-nums' }}>{t('multi.n', { n: v.n })}</Typography>
        </Stack>
      </Box>

      {/* mini stimulus preview */}
      <Box
        sx={{
          aspectRatio: '16/9',
          background: `linear-gradient(135deg, ${accent}55, ${c.navy900})`,
          borderRadius: '10px',
          position: 'relative',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <PlayArrowIcon sx={{ fontSize: 30, color: '#fff' }} />
        <Box component="span" sx={{ position: 'absolute', bottom: 8, left: 10, fontSize: 10, color: 'rgba(255,255,255,.9)', fontVariantNumeric: 'tabular-nums' }}>
          0:30
        </Box>
      </Box>

      <MiniTimeline shape={v.shape} />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 0.75 }}>
        <MiniKPI label={t('multi.kpi.joy')} value={v.joy} good={v.joy > 65} />
        <MiniKPI label={t('multi.kpi.attention')} value={v.att} good={v.att > 70} />
        <MiniKPI label={t('multi.kpi.completion')} value={v.completion} suffix="%" good={v.completion > 80} />
      </Box>
    </Stack>
  )
}

function MiniTimeline({ shape }: { shape: number[] }) {
  const theme = useTheme()
  const joyData = shape
  const attData = shape.map((v, i) => Math.max(0.3, v - 0.08 + Math.sin(i * 0.5) * 0.04))
  const W = 280
  const H = 70
  const buildPath = (d: number[]) =>
    d.map((v, i) => `${i ? 'L' : 'M'}${((i / (d.length - 1)) * W).toFixed(1)} ${((1 - v) * H).toFixed(1)}`).join(' ')
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} width="100%" height={H} sx={{ display: 'block' }}>
      <path d={buildPath(attData)} fill="none" stroke={theme.affexy.emotion.attention} strokeWidth="1.6" opacity="0.6" />
      <path d={buildPath(joyData)} fill="none" stroke={theme.affexy.colors.coral} strokeWidth="2.2" />
    </Box>
  )
}

function MiniKPI({ label, value, good, suffix }: { label: string; value: number; good?: boolean; suffix?: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Stack spacing={0.5} sx={{ px: 1, py: 1.25, bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '10px' }}>
      <Typography sx={{ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: c.ink48, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 18, color: good ? c.success : c.ink, fontVariantNumeric: 'tabular-nums' }}>
        {value}{suffix ?? ''}
      </Typography>
    </Stack>
  )
}
